import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteData, getData, postData, putData } from "../../../../backend/api";

const useBillingApi = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getPlans = useCallback(
    (params = {}) => getData(params, "plan", dispatch, navigate),
    [dispatch, navigate]
  );
  const getProducts = useCallback(
    (params = {}) => getData(params, "product", dispatch, navigate),
    [dispatch, navigate]
  );
  const createProduct = useCallback(
    (payload) => postData(payload, "product", dispatch, navigate),
    [dispatch, navigate]
  );
  const updateProduct = useCallback(
    (payload, id) => putData(payload, `product/${id}`, dispatch, navigate),
    [dispatch, navigate]
  );
  const deleteProduct = useCallback(
    (id) => deleteData({}, "product", id, dispatch, navigate),
    [dispatch, navigate]
  );
  const getProductPrices = useCallback(
    (params = {}) => getData(params, "product-price", dispatch, navigate),
    [dispatch, navigate]
  );
  const createProductPrice = useCallback(
    (payload) => postData(payload, "product-price", dispatch, navigate),
    [dispatch, navigate]
  );
  const updateProductPrice = useCallback(
    (payload, id) =>
      putData(payload, `product-price/${id}`, dispatch, navigate),
    [dispatch, navigate]
  );
  const resolveProductPrice = useCallback(
    (payload) => postData(payload, "product-price/resolve", dispatch, navigate),
    [dispatch, navigate]
  );
  const createPlan = useCallback(
    (payload) => postData(payload, "plan", dispatch, navigate),
    [dispatch, navigate]
  );
  const updatePlan = useCallback(
    (payload, id) => putData(payload, `plan/${id}`, dispatch, navigate),
    [dispatch, navigate]
  );
  const deletePlan = useCallback(
    (id) => deleteData({}, "plan", id, dispatch, navigate),
    [dispatch, navigate]
  );
  const getFeatures = useCallback(
    (params = {}) => getData(params, "plan/feature", dispatch, navigate),
    [dispatch, navigate]
  );
  const createFeature = useCallback(
    (payload) => postData(payload, "plan/feature", dispatch, navigate),
    [dispatch, navigate]
  );
  const updateFeature = useCallback(
    (payload, id) => putData(payload, `plan/feature/${id}`, dispatch, navigate),
    [dispatch, navigate]
  );
  const deleteFeature = useCallback(
    (id) => deleteData({}, "plan/feature", id, dispatch, navigate),
    [dispatch, navigate]
  );
  const setPlanFeatures = useCallback(
    (id, payload) => putData(payload, `plan/${id}/features`, dispatch, navigate),
    [dispatch, navigate]
  );
  const getCompanies = useCallback(
    () => getData({}, "company/select", dispatch, navigate),
    [dispatch, navigate]
  );
  const getCompany = useCallback(
    (id) => getData({ id }, "company", dispatch, navigate),
    [dispatch, navigate]
  );
  const getSubscriptions = useCallback(
    (params = {}) => getData(params, "subscription", dispatch, navigate),
    [dispatch, navigate]
  );
  const createSubscription = useCallback(
    (payload) => postData(payload, "subscription", dispatch, navigate),
    [dispatch, navigate]
  );
  const changeSubscriptionPlan = useCallback(
    (id, payload) =>
      putData(payload, `subscription/${id}/plan`, dispatch, navigate),
    [dispatch, navigate]
  );
  const cancelSubscription = useCallback(
    (id, payload) =>
      putData(payload, `subscription/${id}/cancel`, dispatch, navigate),
    [dispatch, navigate]
  );
  const renewSubscription = useCallback(
    (id, payload) =>
      putData(payload, `subscription/${id}/renew`, dispatch, navigate),
    [dispatch, navigate]
  );
  const toggleAutoRenew = useCallback(
    (id, auto_renew) =>
      putData({ auto_renew }, `subscription/${id}/auto-renew`, dispatch, navigate),
    [dispatch, navigate]
  );
  const pauseSubscription = useCallback(
    (id, payload) =>
      putData(payload, `subscription/${id}/pause`, dispatch, navigate),
    [dispatch, navigate]
  );
  const resumeSubscription = useCallback(
    (id) =>
      putData({}, `subscription/${id}/resume`, dispatch, navigate),
    [dispatch, navigate]
  );
  const getSubscriptionHistory = useCallback(
    (params = {}) => getData(params, "subscription/history", dispatch, navigate),
    [dispatch, navigate]
  );
  const getBillingAlerts = useCallback(
    (params = {}) => getData(params, "subscription/alerts", dispatch, navigate),
    [dispatch, navigate]
  );
  const getRecurringInvoices = useCallback(
    (params = {}) => getData(params, "recurring-invoice", dispatch, navigate),
    [dispatch, navigate]
  );
  const createRecurringInvoiceFromInvoice = useCallback(
    (invoiceId, payload) =>
      postData(
        payload,
        `recurring-invoice/from-invoice/${invoiceId}`,
        dispatch,
        navigate
      ),
    [dispatch, navigate]
  );
  const updateRecurringInvoice = useCallback(
    (id, payload) =>
      putData(payload, `recurring-invoice/${id}`, dispatch, navigate),
    [dispatch, navigate]
  );
  const deleteRecurringInvoice = useCallback(
    (id) => deleteData({}, "recurring-invoice", id, dispatch, navigate),
    [dispatch, navigate]
  );

  return useMemo(
    () => ({
      getPlans,
      getProducts,
      createProduct,
      updateProduct,
      deleteProduct,
      getProductPrices,
      createProductPrice,
      updateProductPrice,
      resolveProductPrice,
      createPlan,
      updatePlan,
      deletePlan,
      getFeatures,
      createFeature,
      updateFeature,
      deleteFeature,
      setPlanFeatures,
      getCompanies,
      getCompany,
      getSubscriptions,
      createSubscription,
      changeSubscriptionPlan,
      cancelSubscription,
      renewSubscription,
      toggleAutoRenew,
      pauseSubscription,
      resumeSubscription,
      getSubscriptionHistory,
      getBillingAlerts,
      getRecurringInvoices,
      createRecurringInvoiceFromInvoice,
      updateRecurringInvoice,
      deleteRecurringInvoice,
    }),
    [
      getPlans,
      getProducts,
      createProduct,
      updateProduct,
      deleteProduct,
      getProductPrices,
      createProductPrice,
      updateProductPrice,
      resolveProductPrice,
      createPlan,
      updatePlan,
      deletePlan,
      getFeatures,
      createFeature,
      updateFeature,
      deleteFeature,
      setPlanFeatures,
      getCompanies,
      getCompany,
      getSubscriptions,
      createSubscription,
      changeSubscriptionPlan,
      cancelSubscription,
      renewSubscription,
      toggleAutoRenew,
      pauseSubscription,
      resumeSubscription,
      getSubscriptionHistory,
      getBillingAlerts,
      getRecurringInvoices,
      createRecurringInvoiceFromInvoice,
      updateRecurringInvoice,
      deleteRecurringInvoice,
    ]
  );
};

export default useBillingApi;
